// src/pages/BusinessProfile.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall, AuthAPI } from "../api";

export default function BusinessProfile() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    legal_name: "",
    display_name: "",
    owner_name: "",
    email: "",
    phone: "",
    website: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    tax_id: "",
    license_number: "",
    timezone: "",
    reply_to_email: "",
    support_email: "",
    current_password: "",
    new_password: "",
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [profileData, meData] = await Promise.all([
          apiCall("/profile"),
          AuthAPI.me(),
        ]);

        setForm((f) => ({ ...f, ...profileData }));
        setIsPlatformAdmin(!!meData?.is_platform_admin);
      } catch (e) {
        setMsg("Could not load profile.");
      }
    })();
  }, []);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg("");

    try {
      const data = await apiCall("/profile", {
        method: "PUT",
        body: JSON.stringify(form),
        headers: { "Content-Type": "application/json" },
      });
      setForm((f) => ({ ...f, ...data }));
      setMsg("Saved!");
    } catch (e) {
      setMsg("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const pageWrap = {
    maxWidth: 900,
    margin: "0 auto",
    padding: 24,
    color: "#ffffff",
  };

  const shellCard = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 18,
    padding: 18,
    background: "#081a2b",
  };

  const sectionCard = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 16,
    background: "#10263a",
  };

  const pageTitle = {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 18,
    margin: 0,
  };

  const subText = {
    color: "#9fb0c3",
    marginTop: 6,
    marginBottom: 0,
    lineHeight: 1.5,
  };

  const sectionHeader = {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 16,
    margin: "0 0 14px",
    letterSpacing: 0.2,
  };

  const labelText = {
    color: "#dbe7f3",
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 600,
  };

  const input = {
    width: "100%",
    padding: 10,
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10,
    background: "#0f2235",
    color: "#ffffff",
    boxSizing: "border-box",
    outline: "none",
  };

  const saveBtn = {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: "#f5c542",
    color: "#0b1622",
    fontWeight: 700,
    cursor: "pointer",
  };

  const platformBtn = {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: "#f5c542",
    color: "#0b1622",
    fontWeight: 700,
    cursor: "pointer",
  };

  const msgOk = msg === "Saved!" || msg === "Password updated successfully";
  const msgStyle = {
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    border: msgOk
      ? "1px solid rgba(125,223,138,0.30)"
      : "1px solid rgba(255,138,138,0.30)",
    background: msgOk ? "rgba(125,223,138,0.10)" : "rgba(255,138,138,0.10)",
    color: msgOk ? "#7ddf8a" : "#ff8a8a",
  };

  return (
    <div style={pageWrap}>
      <div style={shellCard}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h2 style={pageTitle}>Business Profile</h2>

          {isPlatformAdmin && (
            <button
              type="button"
              onClick={() => navigate("/platform")}
              style={platformBtn}
            >
              Platform Management
            </button>
          )}
        </div>

        <p style={subText}>
          This information appears in reports, emails, and letters for{" "}
          <code style={{ color: "#ffffff" }}>this workspace</code>.
        </p>

        {msg && <div style={msgStyle}>{msg}</div>}

        <form onSubmit={onSave} style={{ display: "grid", gap: 14, marginTop: 16 }}>
          <div style={sectionCard}>
            <div style={sectionHeader}>Identity</div>

            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={labelText}>Legal name</div>
              <input
                style={input}
                name="legal_name"
                value={form.legal_name || ""}
                onChange={onChange}
              />
            </label>

            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={labelText}>Public/display name</div>
              <input
                style={input}
                name="display_name"
                value={form.display_name || ""}
                onChange={onChange}
              />
            </label>

            <label style={{ display: "block" }}>
              <div style={labelText}>Owner / Manager / CEO</div>
              <input
                style={input}
                name="owner_name"
                value={form.owner_name || ""}
                onChange={onChange}
              />
            </label>
          </div>

          <div style={sectionCard}>
            <div style={sectionHeader}>Contact</div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <label>
                <div style={labelText}>Email</div>
                <input
                  style={input}
                  name="email"
                  value={form.email || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>Phone</div>
                <input
                  style={input}
                  name="phone"
                  value={form.phone || ""}
                  onChange={onChange}
                />
              </label>
            </div>

            <label style={{ display: "block" }}>
              <div style={labelText}>Website</div>
              <input
                style={input}
                name="website"
                value={form.website || ""}
                onChange={onChange}
              />
            </label>
          </div>

          <div style={sectionCard}>
            <div style={sectionHeader}>Address</div>

            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={labelText}>Address line 1</div>
              <input
                style={input}
                name="address_line1"
                value={form.address_line1 || ""}
                onChange={onChange}
              />
            </label>

            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={labelText}>Address line 2</div>
              <input
                style={input}
                name="address_line2"
                value={form.address_line2 || ""}
                onChange={onChange}
              />
            </label>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label>
                <div style={labelText}>City</div>
                <input
                  style={input}
                  name="city"
                  value={form.city || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>State</div>
                <input
                  style={input}
                  name="state"
                  value={form.state || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>Postal code</div>
                <input
                  style={input}
                  name="postal_code"
                  value={form.postal_code || ""}
                  onChange={onChange}
                />
              </label>
            </div>
          </div>

          <div style={sectionCard}>
            <div style={sectionHeader}>Extras</div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <label>
                <div style={labelText}>Tax ID</div>
                <input
                  style={input}
                  name="tax_id"
                  value={form.tax_id || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>License #</div>
                <input
                  style={input}
                  name="license_number"
                  value={form.license_number || ""}
                  onChange={onChange}
                />
              </label>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label>
                <div style={labelText}>Timezone</div>
                <input
                  style={input}
                  name="timezone"
                  value={form.timezone || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>Reply-To email</div>
                <input
                  style={input}
                  name="reply_to_email"
                  value={form.reply_to_email || ""}
                  onChange={onChange}
                />
              </label>

              <label>
                <div style={labelText}>Support email</div>
                <input
                  style={input}
                  name="support_email"
                  value={form.support_email || ""}
                  onChange={onChange}
                />
              </label>
            </div>
          </div>

          <div style={{ marginTop: 4 }}>
            <button
              disabled={saving}
              type="submit"
              style={{
                ...saveBtn,
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>

          <div style={sectionCard}>
            <div style={sectionHeader}>Change Password</div>

            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <div style={labelText}>Current Password</div>
                <input
                  style={input}
                  type="password"
                  value={form.current_password || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, current_password: e.target.value }))
                  }
                />
              </label>

              <label>
                <div style={labelText}>New Password</div>
                <input
                  style={input}
                  type="password"
                  value={form.new_password || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, new_password: e.target.value }))
                  }
                />
              </label>

              <button
                type="button"
                onClick={async () => {
                  try {
                    setMsg("");

                    const res = await fetch("/api/auth/change-password", {
                      method: "POST",
                      credentials: "include",
                      headers: {
                        "Content-Type": "application/json",
                        "x-tenant": localStorage.getItem("x-tenant"),
                      },
                      body: JSON.stringify({
                        current_password: form.current_password,
                        new_password: form.new_password,
                      }),
                    });

                    if (!res.ok) {
                      const text = await res.text();
                      throw new Error(text);
                    }

                    setForm((f) => ({
                      ...f,
                      current_password: "",
                      new_password: "",
                    }));

                    setMsg("Password updated successfully");
                  } catch (e) {
                    setMsg("Password update failed");
                  }
                }}
                style={saveBtn}
              >
                Update Password
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
